import type { AuditEvent, FieldConflict, UUID } from './types';

/**
 * Field-aware merge for concurrent edits (section 16.3 / 19.3).
 *
 * Given the record the editor started from (base, at `expectedVersion`), the
 * record as it is now (current), and the editor's proposed patch, decide
 * which fields can merge automatically and which collide.
 *
 * A field collides only when it changed on the server since the editor's
 * base AND the editor also changed it to a different value.
 */
export function mergePatch<T extends Record<string, unknown>>(
  base: T,
  current: T,
  patch: Partial<T>,
  changedSinceBase: Map<string, { by: UUID | null; at: string; before: unknown }>,
): { merged: Partial<T>; conflicts: FieldConflict[] } {
  const merged: Partial<T> = {};
  const conflicts: FieldConflict[] = [];
  for (const key of Object.keys(patch) as (keyof T & string)[]) {
    const yours = patch[key];
    const cur = current[key];
    if (deepEqual(yours, cur)) continue; // already the same — nothing to do
    const serverChange = changedSinceBase.get(key);
    if (serverChange && !deepEqual(base[key], cur)) {
      conflicts.push({
        field: key,
        currentValue: cur,
        yourValue: yours,
        baseValue: base[key],
        changedByAccountId: serverChange.by,
        changedAt: serverChange.at,
      });
    } else {
      merged[key] = yours;
    }
  }
  return { merged, conflicts };
}

/** Collect which fields changed on an entity after `sinceVersion` from the audit trail. */
export function changesSince(audit: AuditEvent[], entityId: UUID, sinceVersion: number): Map<string, { by: UUID | null; at: string; before: unknown }> {
  const out = new Map<string, { by: UUID | null; at: string; before: unknown }>();
  const rows = audit
    .filter((a) => a.entityId === entityId && a.entityVersion !== undefined && a.entityVersion > sinceVersion && a.before)
    .sort((a, b) => (a.entityVersion ?? 0) - (b.entityVersion ?? 0));
  for (const row of rows) {
    for (const field of Object.keys(row.before ?? {})) {
      if (!out.has(field)) out.set(field, { by: row.actorAccountId, at: row.timestamp, before: (row.before as Record<string, unknown>)[field] });
    }
  }
  return out;
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a as object).filter((k) => (a as Record<string, unknown>)[k] !== undefined);
    const kb = Object.keys(b as object).filter((k) => (b as Record<string, unknown>)[k] !== undefined);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

/** Diff two flat records → {before, after} containing only changed fields. */
export function diffRecords<T extends Record<string, unknown>>(before: T, after: T): { before: Record<string, unknown>; after: Record<string, unknown> } {
  const b: Record<string, unknown> = {};
  const a: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const k of keys) {
    if (k === 'version' || k === 'updatedAt' || k === 'updatedBy') continue;
    if (!deepEqual(before[k], after[k])) {
      b[k] = before[k];
      a[k] = after[k];
    }
  }
  return { before: b, after: a };
}
